import { useCallback, useEffect, useRef, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

const STORAGE_PREFIX = 'photostat-command-center';
const POLL_INTERVAL = 10 * 1000;

export interface CommandCenterMedia {
    activationId: number;
    photo: MediaInstance | null;
    isLoading: boolean;
}

function storageKey(orgId: number) {
    return `${STORAGE_PREFIX}-${orgId}`;
}

function loadSelectedIds(orgId: number): number[] {
    try {
        const stored = localStorage.getItem(storageKey(orgId));
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function saveSelectedIds(orgId: number, ids: number[]) {
    localStorage.setItem(storageKey(orgId), JSON.stringify(ids));
}

export default function useCommandCenter() {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [selectedIds, setSelectedIds] = useState<number[]>(() =>
        loadSelectedIds(orgId),
    );
    const [mediaMap, setMediaMap] = useState<
        Record<
            number,
            { photo: MediaInstance | null; photoCount: number; isLoading: boolean }
        >
    >({});
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Reset selection when org changes
    useEffect(() => {
        setSelectedIds(loadSelectedIds(orgId));
        setMediaMap({});
    }, [orgId]);

    const fetchActivationMedia = useCallback(
        async (activationId: number) => {
            try {
                const response = await authenticatedApi.get(
                    `/activations/${activationId}/photos`,
                );
                const photos: MediaInstance[] = response.data;
                // Sort by id desc to get latest
                photos.sort((a, b) => b.id - a.id);
                return { photo: photos[0] || null, photoCount: photos.length };
            } catch {
                return { photo: null, photoCount: 0 };
            }
        },
        [authenticatedApi],
    );

    const fetchAllMedia = useCallback(
        async (ids: number[]) => {
            if (ids.length === 0) return;

            // Mark all as loading on first fetch only (don't flicker on poll)
            setMediaMap((prev) => {
                const next = { ...prev };
                for (const id of ids) {
                    if (!next[id]) {
                        next[id] = { photo: null, photoCount: 0, isLoading: true };
                    }
                }
                return next;
            });

            const results = await Promise.allSettled(
                ids.map(async (id) => ({
                    id,
                    ...(await fetchActivationMedia(id)),
                })),
            );

            setMediaMap((prev) => {
                const next = { ...prev };
                for (const result of results) {
                    if (result.status === 'fulfilled') {
                        const { id, photo, photoCount } = result.value;
                        next[id] = { photo, photoCount, isLoading: false };
                    }
                }
                return next;
            });
        },
        [fetchActivationMedia],
    );

    // Start/stop polling when selectedIds change
    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (selectedIds.length > 0) {
            fetchAllMedia(selectedIds);
            intervalRef.current = setInterval(() => {
                fetchAllMedia(selectedIds);
            }, POLL_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [selectedIds, fetchAllMedia]);

    const updateSelectedIds = useCallback(
        (ids: number[]) => {
            setSelectedIds(ids);
            saveSelectedIds(orgId, ids);
        },
        [orgId],
    );

    const removeActivation = useCallback(
        (id: number) => {
            const next = selectedIds.filter((i) => i !== id);
            updateSelectedIds(next);
            setMediaMap((prev) => {
                const copy = { ...prev };
                delete copy[id];
                return copy;
            });
        },
        [selectedIds, updateSelectedIds],
    );

    return {
        selectedIds,
        updateSelectedIds,
        removeActivation,
        mediaMap,
    };
}
